import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import CommonLayout from "../../../layouts/CommonLayout";
import { allAPiServicesCall } from "../../../services/apiServices";
import { getAuthConfig } from "../../../services/apiUtils";
import { toast } from "react-toastify";
import {
  FaBell,
  FaPaperPlane,
  FaUsers,
  FaUser,
  FaSearch,
  FaBullhorn,
  FaInfoCircle,
  FaSpinner,
  FaCheckCircle,
  FaTimesCircle,
  FaHistory,
  FaTrash,
} from "react-icons/fa";
import "./SendNotification.css";

const PAGE_SIZE = 20;

export default function SendNotification() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [sendingNotification, setSendingNotification] = useState(false);

  // Form state
  const [notificationType, setNotificationType] = useState("announcement"); // announcement or system
  const [targetType, setTargetType] = useState("all"); // all, specific, multiple
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [link, setLink] = useState("");

  // User search state
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [isSearching, setIsSearching] = useState(false);

  // Notification history state
  const [notifications, setNotifications] = useState([]);
  const [notificationStats, setNotificationStats] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [historyPagination, setHistoryPagination] = useState({
    page: 1,
    limit: PAGE_SIZE,
    total: 0,
    totalPages: 1,
  });

  // Fetch notification history
  const fetchNotifications = useCallback(async (page = 1) => {
    try {
      setHistoryLoading(true);
      const response = await allAPiServicesCall.getAdminNotifications(
        { page, limit: PAGE_SIZE },
        getAuthConfig()
      );

      if (response?.success) {
        setNotifications(response.data || []);
        setHistoryPagination(
          response.pagination || { page: 1, limit: PAGE_SIZE, total: 0, totalPages: 1 }
        );
      }
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  // Fetch notification stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await allAPiServicesCall.getNotificationStats(getAuthConfig());
      if (response?.success) {
        setNotificationStats(response.data);
      }
    } catch (error) {
      console.error("Error fetching notification stats:", error);
    }
  }, []);

  useEffect(() => {
    fetchNotifications(1);
    fetchStats();
  }, [fetchNotifications, fetchStats]);

  // Search users
  const handleSearchUsers = async () => {
    if (!searchQuery.trim()) return;

    try {
      setIsSearching(true);
      const response = await allAPiServicesCall.searchUsersForNotification(
        { search: searchQuery },
        getAuthConfig()
      );

      if (response?.success) {
        setSearchResults(response.data || []);
      }
    } catch (error) {
      console.error("Error searching users:", error);
      toast.error("Failed to search users");
    } finally {
      setIsSearching(false);
    }
  };

  // Add user to selected
  const handleAddUser = (user) => {
    if (!selectedUsers.find((u) => u._id === user._id)) {
      setSelectedUsers([...selectedUsers, user]);
    }
    setSearchQuery("");
    setSearchResults([]);
  };

  // Remove user from selected
  const handleRemoveUser = (userId) => {
    setSelectedUsers(selectedUsers.filter((u) => u._id !== userId));
  };

  // Send notification
  const handleSendNotification = async (e) => {
    e.preventDefault();

    if (!title.trim()) {
      toast.error("Please enter a notification title");
      return;
    }

    if (!message.trim()) {
      toast.error("Please enter a notification message");
      return;
    }

    if (targetType === "specific" && selectedUsers.length === 0) {
      toast.error("Please select at least one user");
      return;
    }

    if (targetType === "multiple" && selectedUsers.length === 0) {
      toast.error("Please select at least one user");
      return;
    }

    try {
      setSendingNotification(true);
      let response;

      const payload = {
        type: notificationType,
        title: title.trim(),
        message: message.trim(),
        link: link.trim() || undefined,
      };

      if (targetType === "all") {
        response = await allAPiServicesCall.sendNotificationToAll(payload, getAuthConfig());
      } else if (targetType === "specific") {
        // _id is already normalized to string by searchUsersForNotification
        response = await allAPiServicesCall.sendNotificationToUser(
          { ...payload, userId: selectedUsers[0]._id },
          getAuthConfig()
        );
      } else {
        // _id is already normalized to string by searchUsersForNotification
        response = await allAPiServicesCall.sendNotificationToMultiple(
          { ...payload, userIds: selectedUsers.map((u) => u._id) },
          getAuthConfig()
        );
      }

      if (response?.success) {
        toast.success(response.message || "Notification sent successfully!");
        // Reset form
        setTitle("");
        setMessage("");
        setLink("");
        setSelectedUsers([]);
        setTargetType("all");
        // Refresh history
        fetchNotifications(1);
        fetchStats();
      } else {
        toast.error(response?.message || "Failed to send notification");
      }
    } catch (error) {
      console.error("Error sending notification:", error);
      toast.error("Failed to send notification");
    } finally {
      setSendingNotification(false);
    }
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    // Check if date is valid
    if (isNaN(date.getTime())) return "-";
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const goToPage = (page) => {
    fetchNotifications(page);
  };

  // Delete all notifications (for testing/reset)
  const handleDeleteAll = async () => {
    if (!window.confirm("Are you sure you want to delete ALL notifications? This cannot be undone.")) {
      return;
    }

    try {
      setHistoryLoading(true);
      const response = await allAPiServicesCall.deleteAllNotifications(getAuthConfig());

      if (response?.success) {
        toast.success(response.message || "All notifications deleted");
        // Refresh data
        fetchNotifications(1);
        fetchStats();
      } else {
        toast.error("Failed to delete notifications");
      }
    } catch (error) {
      console.error("Error deleting all notifications:", error);
      toast.error("Failed to delete notifications");
    } finally {
      setHistoryLoading(false);
    }
  };

  return (
    <CommonLayout>
      <div className="send-notification-page">
        {/* Header */}
        <div className="page-header">
          <div className="header-content">
            <div className="header-icon">
              <FaBell size={32} />
            </div>
            <div className="header-text">
              <h1>Send Notification</h1>
              <p>Send announcements and system messages to users</p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        {notificationStats && (
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-icon total">
                <FaBell size={24} />
              </div>
              <div className="stat-content">
                <span className="stat-value">{notificationStats.totalNotifications || 0}</span>
                <span className="stat-label">Total Sent</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon announcement">
                <FaBullhorn size={24} />
              </div>
              <div className="stat-content">
                <span className="stat-value">
                  {notificationStats.byType?.announcement || 0}
                </span>
                <span className="stat-label">Announcements</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon system">
                <FaInfoCircle size={24} />
              </div>
              <div className="stat-content">
                <span className="stat-value">{notificationStats.byType?.system || 0}</span>
                <span className="stat-label">System</span>
              </div>
            </div>
            <div className="stat-card">
              <div className="stat-icon read">
                <FaCheckCircle size={24} />
              </div>
              <div className="stat-content">
                <span className="stat-value">{notificationStats.readCount || 0}</span>
                <span className="stat-label">Read</span>
              </div>
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="notification-content">
          {/* Send Form */}
          <div className="notification-form-card">
            <div className="card-header">
              <FaPaperPlane size={20} />
              <h2>Compose Notification</h2>
            </div>

            <form onSubmit={handleSendNotification} className="notification-form">
              {/* Notification Type */}
              <div className="form-group">
                <label>Notification Type</label>
                <div className="type-selector">
                  <button
                    type="button"
                    className={`type-btn ${notificationType === "announcement" ? "active" : ""}`}
                    onClick={() => setNotificationType("announcement")}
                  >
                    <FaBullhorn size={16} />
                    Announcement
                  </button>
                  <button
                    type="button"
                    className={`type-btn ${notificationType === "system" ? "active" : ""}`}
                    onClick={() => setNotificationType("system")}
                  >
                    <FaInfoCircle size={16} />
                    System
                  </button>
                </div>
              </div>

              {/* Target Selection */}
              <div className="form-group">
                <label>Send To</label>
                <div className="target-selector">
                  <button
                    type="button"
                    className={`target-btn ${targetType === "all" ? "active" : ""}`}
                    onClick={() => {
                      setTargetType("all");
                      setSelectedUsers([]);
                    }}
                  >
                    <FaUsers size={16} />
                    All Users
                  </button>
                  <button
                    type="button"
                    className={`target-btn ${targetType === "specific" ? "active" : ""}`}
                    onClick={() => {
                      setTargetType("specific");
                      setSelectedUsers([]);
                    }}
                  >
                    <FaUser size={16} />
                    Specific User
                  </button>
                  <button
                    type="button"
                    className={`target-btn ${targetType === "multiple" ? "active" : ""}`}
                    onClick={() => {
                      setTargetType("multiple");
                      setSelectedUsers([]);
                    }}
                  >
                    <FaUsers size={16} />
                    Multiple Users
                  </button>
                </div>
              </div>

              {/* User Search (for specific/multiple) */}
              {(targetType === "specific" || targetType === "multiple") && (
                <div className="form-group">
                  <label>Search Users</label>
                  <div className="user-search-container">
                    <div className="search-input-wrapper">
                      <input
                        type="text"
                        placeholder="Search by username or email..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), handleSearchUsers())}
                      />
                      <button
                        type="button"
                        onClick={handleSearchUsers}
                        disabled={isSearching}
                        className="search-btn"
                      >
                        {isSearching ? <FaSpinner className="spin" /> : <FaSearch />}
                      </button>
                    </div>

                    {/* Search Results */}
                    {searchResults.length > 0 && (
                      <div className="search-results">
                        {searchResults.map((user) => (
                          <div
                            key={user._id}
                            className="search-result-item"
                            onClick={() => handleAddUser(user)}
                          >
                            <div className="user-avatar">
                              {user.username?.charAt(0).toUpperCase() || "?"}
                            </div>
                            <div className="user-info">
                              <span className="username">{user.username}</span>
                              <span className="email">{user.email}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Selected Users */}
                    {selectedUsers.length > 0 && (
                      <div className="selected-users">
                        {selectedUsers.map((user) => (
                          <div key={user._id} className="selected-user-chip">
                            <span>{user.username}</span>
                            <button
                              type="button"
                              onClick={() => handleRemoveUser(user._id)}
                              className="remove-btn"
                            >
                              <FaTimesCircle />
                            </button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Title */}
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  placeholder="Enter notification title..."
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={100}
                  required
                />
                <span className="char-count">{title.length}/100</span>
              </div>

              {/* Message */}
              <div className="form-group">
                <label>Message *</label>
                <textarea
                  placeholder="Enter notification message..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  maxLength={500}
                  rows={4}
                  required
                />
                <span className="char-count">{message.length}/500</span>
              </div>

              {/* Link (optional) */}
              <div className="form-group">
                <label>Link (Optional)</label>
                <input
                  type="text"
                  placeholder="e.g., /songs or /artists"
                  value={link}
                  onChange={(e) => setLink(e.target.value)}
                />
                <span className="help-text">
                  Add a link where users will be directed when they click the notification
                </span>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                className="send-btn"
                disabled={sendingNotification}
              >
                {sendingNotification ? (
                  <>
                    <FaSpinner className="spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <FaPaperPlane />
                    Send Notification
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Notification History */}
          <div className="notification-history-card">
            <div className="card-header">
              <FaHistory size={20} />
              <h2>Recent Notifications Sent</h2>
              {notifications.length > 0 && (
                <button
                  onClick={handleDeleteAll}
                  className="clear-all-btn"
                  title="Delete all notifications"
                >
                  <FaTrash size={14} />
                  Clear All
                </button>
              )}
            </div>

            <div className="history-list">
              {historyLoading ? (
                <div className="loading-state">
                  <FaSpinner className="spin" size={24} />
                  <span>Loading notifications...</span>
                </div>
              ) : notifications.length === 0 ? (
                <div className="empty-state">
                  <FaBell size={48} />
                  <p>No notifications sent yet</p>
                </div>
              ) : (
                <>
                  {notifications.map((notification) => (
                    <div key={notification._id} className="history-item">
                      <div className="history-icon">
                        {notification.type === "announcement" ? (
                          <FaBullhorn />
                        ) : (
                          <FaInfoCircle />
                        )}
                      </div>
                      <div className="history-content">
                        <h4>{notification.title}</h4>
                        <p>{notification.message}</p>
                        <div className="history-meta">
                          <span className={`type-badge ${notification.type}`}>
                            {notification.type}
                          </span>
                          {notification.recipientCount > 1 && (
                            <span className="recipient-count">
                              {notification.recipientCount} recipients
                            </span>
                          )}
                          <span className="date">{formatDate(notification.createdAt)}</span>
                        </div>
                      </div>
                    </div>
                  ))}

                  {/* Pagination */}
                  {historyPagination.totalPages > 1 && (
                    <div className="pagination">
                      <button
                        onClick={() => goToPage(historyPagination.page - 1)}
                        disabled={historyPagination.page <= 1}
                        className="page-btn"
                      >
                        Previous
                      </button>
                      <span className="page-info">
                        Page {historyPagination.page} of {historyPagination.totalPages}
                      </span>
                      <button
                        onClick={() => goToPage(historyPagination.page + 1)}
                        disabled={historyPagination.page >= historyPagination.totalPages}
                        className="page-btn"
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </CommonLayout>
  );
}
